import Action from "../base/action";
import LoadTimeline from "../base/load-timeline";
import MenuManager from "./menu-manager";

const _addMenuStateByActionId = new Map();

class AddMenu extends Action {
    properties;
    menuType;
    styleId;
    menuController;
    _commands;
    until;

    constructor(props) {
        super(props);
        this.properties = props;
        this.menuType = props.menuType || 'splash_screen';
        this.styleId = props.styleId;
        this.until = props.until;
        this._commands = [];
    }

    async execute(eventLoop) {
        try {
            const props = this.properties;
            const styles = eventLoop.training.resources[this.styleId];

            const titleText = props.titleText
                ? this.interpolateVariableValues(eventLoop.training, props.titleText)
                : props.titleText;
            const bodyText = props.bodyText
                ? this.interpolateVariableValues(eventLoop.training, props.bodyText)
                : props.bodyText;

            const buttons = this._convertButtonsToArray(props);
            const commands = [];

            buttons.forEach(button => {
                const hasTimelines = button.timelines && button.timelines.length > 0;
                const hasEvents = button.events && button.events.length > 0;
                const triggerType = button.triggerType || 'Timeline';
                if (hasTimelines || hasEvents) {
                    const commandAction = new LoadTimeline({
                        timelines: button.timelines || [],
                        events: button.events || [],
                        triggerType: triggerType,
                        preserveActionList: [this.type]
                    });
                    commands.push({
                        name: button.name,
                        callback: async () => {
                            try {
                                await commandAction.execute(eventLoop);
                            } catch (error) {
                                console.error(`AddMenu action: Error executing button "${button.name}" ${triggerType.toLowerCase()}:`, error);
                            }
                        }
                    });
                } else {
                    commands.push({
                        name: button.name,
                        callback: () => {}
                    });
                }
            });

            const styleSubObject = {
                ...this._extractStyleProperties(styles),
                VRMode: props.VRMode,
                NonVRMode: props.NonVRMode
            };
            const mergedButtons = this._mergeButtonsWithStyle(buttons, styleSubObject.buttons, commands);

            let backButtonAction = null;
            if (props.backButton) {
                const backButton = props.backButton;
                const hasTimelines = backButton.timelines && backButton.timelines.length > 0;
                const hasEvents = backButton.events && backButton.events.length > 0;
                const triggerType = backButton.triggerType || 'Timeline';
                
                if (hasTimelines || hasEvents) {
                    const backButtonCommandAction = new LoadTimeline({
                        timelines: backButton.timelines || [],
                        events: backButton.events || [],
                        triggerType: triggerType,
                        preserveActionList: [this.type]
                    });
                    backButtonAction = async () => {
                        try {
                            await backButtonCommandAction.execute(eventLoop);
                        } catch (error) {
                            console.error(`AddMenu action: Error executing backButton ${triggerType.toLowerCase()}:`, error);
                        }
                    };
                }
            }

            const content = {
                titleText,
                bodyText,
                subtext: props.subtext,
                image: props.image ? {
                    ...props.image,
                    zoom: props.image.zoom ?? 1,
                    offsetX: props.image.offsetX ?? 0,
                    offsetY: props.image.offsetY ?? 0
                } : null,
                backButton: props.backButton ? {
                    ...props.backButton,
                    action: backButtonAction
                } : { action: backButtonAction },
                buttons: mergedButtons,
                actionId: this.actionId
            };

            if (props.optionCards && Array.isArray(props.optionCards)) {
                content.optionCards = props.optionCards.map((card, idx) => {
                    const command = commands.find(cmd => cmd.name === `option${String.fromCharCode(65 + idx)}`);
                    return {
                        ...card,
                        label: card?.label !== undefined ? card.label : undefined,
                        zoom: card?.zoom ?? 1,
                        offsetX: card?.offsetX ?? 0,
                        offsetY: card?.offsetY ?? 0,
                        action: command?.callback
                    };
                });
            } else if (props.optionA || props.optionB || props.optionC) {
                const optionCards = [];
                const optionKeys = ['optionA', 'optionB', 'optionC'];

                optionKeys.forEach((key, idx) => {
                    if (props[key]) {
                        const option = props[key];
                        const command = commands.find(cmd => cmd.name === `option${String.fromCharCode(65 + idx)}`);
                        optionCards.push({
                            label: option.label !== undefined ? option.label : undefined,
                            title: option.label ?? '',
                            imageType: option.imageType || 'icon',
                            selectedIcon: option.selectedIcon || null,
                            imageUrl: option.imageUrl || null,
                            imageResourceId: option.imageResourceId || null,
                            zoom: option.zoom ?? 1,
                            offsetX: option.offsetX ?? 0,
                            offsetY: option.offsetY ?? 0,
                            buttonText: option.buttonText ?? '',  // Use ?? to preserve empty strings
                            timelines: option.timelines || [],
                            action: command?.callback
                        });
                    }
                });

                if (optionCards.length > 0) {
                    content.optionCards = optionCards;
                }
            }

            if (props.pagination) {
                content.pagination = typeof props.pagination === 'object' && props.pagination !== null
                    ? { ...props.pagination }
                    : props.pagination;
            }

            const sceneConfig = this._transformSceneObject(styleSubObject.Scene || props.Scene);

            const menuConfig = {
                menuType: this.menuType,
                style: styleSubObject,
                content: content,
                scene: sceneConfig
            };


            const usedOverall = typeof OverallMenuController !== 'undefined' && OverallMenuController !== null;
            const controller = usedOverall
                ? await OverallMenuController.createMenu(menuConfig)
                : await MenuManager.createMenu(this.actionId, menuConfig);
            _addMenuStateByActionId.set(this.actionId, { source: usedOverall ? 'overall' : 'manager' });
        } catch (error) {
            console.error('AddMenu action: Error executing menu action:', error);
            throw error;
        }
    }

    _toPlainTimelines(arr) {
        return Array.isArray(arr) ? arr.slice() : [];
    }

    _convertButtonsToArray(props) {
        const buttons = [];

        if (props.buttonLeft || props.buttonCenter || props.buttonRight) {
            if (props.buttonLeft) {
                buttons.push({
                    name: 'left',
                    text: props.buttonLeft.buttonText ?? '',
                    enabled: props.buttonLeft.enabled !== false,
                    timelines: this._toPlainTimelines(props.buttonLeft.timelines),
                    events: this._toPlainTimelines(props.buttonLeft.events),
                    triggerType: props.buttonLeft.triggerType || 'Timeline'
                });
            }
            if (props.buttonCenter) {
                buttons.push({
                    name: 'center',
                    text: props.buttonCenter.buttonText ?? '',
                    enabled: props.buttonCenter.enabled !== false,
                    timelines: this._toPlainTimelines(props.buttonCenter.timelines),
                    events: this._toPlainTimelines(props.buttonCenter.events),
                    triggerType: props.buttonCenter.triggerType || 'Timeline'
                });
            }
            if (props.buttonRight) {
                buttons.push({
                    name: 'right',
                    text: props.buttonRight.buttonText ?? '',
                    enabled: props.buttonRight.enabled !== false,
                    timelines: this._toPlainTimelines(props.buttonRight.timelines),
                    events: this._toPlainTimelines(props.buttonRight.events),
                    triggerType: props.buttonRight.triggerType || 'Timeline'
                });
            }
        }
        else if (props.button1 || props.button2 || props.button3 || props.button4 || props.button5 || props.button6) {
            const maxButtons = 6;
            for (let i = 1; i <= maxButtons; i++) {
                const buttonKey = `button${i}`;
                const buttonData = props[buttonKey];
                if (buttonData) {
                    buttons.push({
                        name: buttonKey,
                        text: buttonData.buttonText ?? '',
                        enabled: buttonData.enabled !== undefined ? buttonData.enabled : (i <= 2),
                        timelines: this._toPlainTimelines(buttonData.timelines),
                        events: this._toPlainTimelines(buttonData.events),
                        triggerType: buttonData.triggerType || 'Timeline'
                    });
                }
            }
        }
        else if (props.buttons && Array.isArray(props.buttons)) {
            return props.buttons.map((btn, idx) => ({
                name: btn.name || `button${idx + 1}`,
                text: btn.buttonText ?? '',
                enabled: btn.enabled !== undefined ? btn.enabled : true,
                timelines: this._toPlainTimelines(btn.timelines),
                events: this._toPlainTimelines(btn.events),
                triggerType: btn.triggerType || 'Timeline'
            }));
        }
        else if (props.optionCards && Array.isArray(props.optionCards)) {
            return props.optionCards.map((card, idx) => ({
                name: `option${String.fromCharCode(65 + idx)}`,
                text: card.buttonText || `Option ${String.fromCharCode(65 + idx)}`,
                enabled: true,
                timelines: this._toPlainTimelines(card.timelines),
                events: this._toPlainTimelines(card.events),
                triggerType: card.triggerType || 'Timeline',
                _isOptionCard: true
            }));
        }
        else if (props.optionA || props.optionB || props.optionC) {
            const optionKeys = ['optionA', 'optionB', 'optionC'];
            optionKeys.forEach((key, idx) => {
                if (props[key]) {
                    buttons.push({
                        name: `option${String.fromCharCode(65 + idx)}`,
                        text: props[key].buttonText || `Option ${String.fromCharCode(65 + idx)}`,
                        enabled: true,
                        timelines: this._toPlainTimelines(props[key].timelines),
                        events: this._toPlainTimelines(props[key].events),
                        triggerType: props[key].triggerType || 'Timeline',
                        _isOptionCard: true
                    });
                }
            });
        }

        return buttons;
    }

    _extractStyleProperties(styles) {
        if (!styles || !styles.properties) {
            return {};
        }

        const styleProps = styles.properties;
        const Scene = styleProps.Scene && typeof styleProps.Scene === 'object' ? { ...styleProps.Scene } : styleProps.Scene;
        const Camera = styleProps.Camera && typeof styleProps.Camera === 'object' ? { ...styleProps.Camera } : styleProps.Camera;

        return {
            accentColor: styleProps.accentColor,
            textColor: styleProps.textColor,
            titleColor: styleProps.titleColor,
            iconColor: styleProps.iconColor,
            buttonTextColor: styleProps.buttonTextColor,
            outlineColor: styleProps.outlineColor,
            backgroundColor: styleProps.backgroundColor,
            titleScreenBackgroundColor: styleProps.titleScreenBackgroundColor,
            buttonBackgroundColor: styleProps.buttonBackgroundColor,
            buttonHoverColor: styleProps.buttonHoverColor,
            selectedColor: styleProps.selectedColor,
            opacity: styleProps.opacity,
            cardOpacity: styleProps.cardOpacity,
            NonVRMode: styleProps.NonVRMode,
            VRMode: styleProps.VRMode,
            Camera,
            Scene
        };
    }

    _mergeButtonsWithStyle(contentButtons, styleButtons, commands) {
        return contentButtons.map((btn, idx) => {
            const command = commands[idx];

            return {
                name: btn.name,
                text: btn.text,
                enabled: btn.enabled,
                timelines: btn.timelines,
                action: command?.callback
            };
        });
    }

    _transformSceneObject(sceneObj) {
        if (!sceneObj) {
            return {
                position: [0, 2, -2],
                quaternion: [0, 0, 0, 1],
                scale: 1
            };
        }

        const position = Array.isArray(sceneObj.position)
            ? [...sceneObj.position]
            : [
                sceneObj.position?.x ?? 0,
                sceneObj.position?.y ?? 2,
                sceneObj.position?.z ?? -2
            ];
        const quaternion = Array.isArray(sceneObj.quaternion)
            ? [...sceneObj.quaternion]
            : [
                sceneObj.quaternion?.x ?? 0,
                sceneObj.quaternion?.y ?? 0,
                sceneObj.quaternion?.z ?? 0,
                sceneObj.quaternion?.w ?? 1
            ];
        const scale = typeof sceneObj.scale === 'number'
            ? sceneObj.scale
            : (sceneObj.scale?.x ?? 1);

        return { position, quaternion, scale };
    }

    async stop() {
        try {
            const state = _addMenuStateByActionId.get(this.actionId);
            if (state) {
                if (state.source === 'overall' && typeof OverallMenuController !== 'undefined') {
                    OverallMenuController.removeCurrentMenu();
                } else {
                    await MenuManager.removeMenu(this.actionId, false);
                }
                _addMenuStateByActionId.delete(this.actionId);
            }
        } catch (error) {
            console.error('AddMenu action: Error stopping menu:', error);
        }
    }

    async cancel() {
        try {
            const state = _addMenuStateByActionId.get(this.actionId);
            if (state) {
                if (state.source === 'overall' && typeof OverallMenuController !== 'undefined') {
                    OverallMenuController.forceRemoveAllMenus();
                } else {
                    await MenuManager.removeMenu(this.actionId, true);
                }
                _addMenuStateByActionId.delete(this.actionId);
            }
        } catch (error) {
            console.error('AddMenu action: Error cancelling menu:', error);
        }
    }
}

export default AddMenu;

